import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { theme } from '../../../../theme/theme'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { societyWidgetCreateSchema } from '../../../../forms/schemas/society_widget_create'
import { CreateUpdateWidgetForm } from '../CreateUpdateWidgetForm'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { ISocietyWidgetModel } from '../../../../interfaces/models/society-widgets.interfaces'
import { createWidget, patchWidget } from '../../../../api/society-widgets'
import { prependHttp } from '../../../../helpers/url'

interface CreateUpdateWidgetModalProps {
  show: boolean
  close: () => void
  widget?: ISocietyWidgetModel
  children?: React.ReactNode
  society: ISocietiesModel
}

export const CreateUpdateWidgetModal = ({
  show,
  close,
  widget,
  children,
  society,
}: CreateUpdateWidgetModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!widget

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate(
        updateMode
          ? 'functionSettingsView.flashMessage.updateWidgetSuccess'
          : 'functionSettingsView.flashMessage.createWidgetSuccess'
      )
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        updateMode
          ? 'functionSettingsView.flashMessage.updateWidgetFailure'
          : 'functionSettingsView.flashMessage.createWidgetFailure'
      )
    )
  }

  const { mutate: mutatePatchWidget, isPending: isPatchingWidget } =
    useMutation({
      mutationFn: patchWidget,
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const { mutate: mutateCreateWidget, isPending: isCreatingWidget } =
    useMutation({
      mutationFn: createWidget,
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyWidgetCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchWidget({
        id: widget._id,
        body: {
          ...data,
          ...(data.link ? { link: prependHttp(data.link) } : {}),
        },
      })
    } else {
      mutateCreateWidget(data)
    }
  }

  const getModalTitle = (): string => {
    return translate(
      updateMode
        ? 'functionSettingsView.modal.editTitle'
        : 'functionSettingsView.modal.createTitle'
    )
  }

  const loading = isPatchingWidget || isCreatingWidget

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div>
                <p style={theme.textVariants.h3}>{getModalTitle()}</p>
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateWidgetForm
                society={society}
                onError={showErrorMessage}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                widget={widget}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
